import { useMemo } from 'react';

export default function SolarStructure({
  tileAngleDeg = 12,
  lowestLegHeight = 2.5,
  tableCols = 3,
  tableRows = 2,
  setStructureData = () => {},
}) {
  // === CONFIGURATION ===
  const tiltRadians = (tileAngleDeg * Math.PI) / 180;
  const legRadius = 0.07;
  const panelWidth = 1.2;
  const panelLength = panelWidth * 2;
  const panelSpacing = 0;
  const spacingX = panelWidth + 0.08;
  const spacingZ = panelWidth + 1.275;
  const panelColor = '#00008B';
  const rafterOverhangPct = 0.25;
  const purlinThickness = 0.075;
  const purlinOverhang = 0.8;

  // === DERIVED VALUES ===
  const totalSpanZ = spacingZ * (tableRows - 1);
  const highestLegHeight = lowestLegHeight + Math.tan(tiltRadians) * totalSpanZ;
  const rafterRise = highestLegHeight - lowestLegHeight;
  const rafterLength =
    Math.sqrt(totalSpanZ ** 2 + rafterRise ** 2) * (1 + 2 * rafterOverhangPct);

  const legCols = Math.max(2, Math.ceil(tableCols / 3) + 1);
  const legRows = Math.max(2, Math.ceil(tableRows / 2) + 1);

  // === Legs ===
  const legs = [];
  const legHeightMap = {};

  for (let row = 0; row < legRows; row++) {
    for (let col = 0; col < legCols; col++) {
      const x = (col * spacingX * (tableCols - 1)) / (legCols - 1);
      const z = (row * spacingZ * (tableRows - 1)) / (legRows - 1);
      const height = highestLegHeight - (z / totalSpanZ) * rafterRise;
      legs.push({ x, z, height });
      const rounded = height.toFixed(2);
      legHeightMap[rounded] = (legHeightMap[rounded] || 0) + 1;
    }
  }

  // === Rafters ===
  const rafters = [];
  for (let col = 0; col < legCols; col++) {
    const x = (col * spacingX * (tableCols - 1)) / (legCols - 1);
    const z1 = 0;
    const z2 = totalSpanZ;
    const y1 = highestLegHeight - (z1 / totalSpanZ) * rafterRise;
    const y2 = highestLegHeight - (z2 / totalSpanZ) * rafterRise;
    const length = Math.sqrt((z2 - z1) ** 2 + (y2 - y1) ** 2) + 2.5;
    const angle = Math.atan2(y2 - y1, z2 - z1);
    const midZ = (z1 + z2) / 2;
    const midY = (y1 + y2) / 2;
    rafters.push({ x, y: midY, z: midZ, length, angle });
  }

  // === Purlins ===
  const purlins = [];
  const purlinLength = spacingX * (tableCols - 1) + 2 * purlinOverhang;
  const purlinYBuffer = 0.05;

  for (let row = 0; row < tableRows; row++) {
    const zStart = row * spacingZ;
    const zEnd = zStart + panelWidth;
    const z20 = zStart + (zEnd - zStart) * -0.5;
    const z80 = zStart + (zEnd - zStart) * 0.5;
    [z20, z80].forEach((z) => {
      const y =
        highestLegHeight - (z / totalSpanZ) * rafterRise + purlinYBuffer;
      purlins.push({ y, z });
    });
  }

  // === Panels ===
  const panels = [];
  for (let row = 0; row < tableRows; row++) {
    for (let col = 0; col < tableCols; col++) {
      const x = col * spacingX;
      const z = row * spacingZ;
      const y = highestLegHeight - (z / totalSpanZ) * rafterRise + 0.12;
      panels.push({ x, y, z });
    }
  }

  // === Ground Span & Area Calculations ===
  const eastWestSpan = spacingX * (tableCols - 1);
  const northSouthSpan = spacingZ * (tableRows - 1);
  const legArea = (legCols - 1) * spacingX * (legRows - 1) * spacingZ;
  const panelTotalLength =
    panelLength * tableCols + panelSpacing * (tableCols - 1);
  const panelTotalWidth =
    panelWidth * tableRows + panelSpacing * (tableRows - 1);
  const panelArea = panelTotalLength * panelTotalWidth;

  // === Clamps & Fasteners ===
  const midClampsPerRow = (tableCols - 1) * 2;
  const totalMidClamps = tableRows * midClampsPerRow;
  const endClampsPerRow = 4;
  const totalEndClamps = tableRows * endClampsPerRow;
  const basePlates = legs.length;
  const rafterToLegJoints = legs.length;
  const rafterToPurlinJoints = rafters.length * purlins.length;
  const nutBoltPairs = rafterToLegJoints + rafterToPurlinJoints;

  // === Updated Structure Summary ===
  const structureSummary = useMemo(
    () => ({
      tilt_degrees: tileAngleDeg,
      leg_heights: legHeightMap,
      rafter_length: {
        count: rafters.length,
        length: rafters[0]?.length?.toFixed(2),
      },
      purlin_length: {
        count: purlins.length,
        length: purlinLength.toFixed(2),
        overhang_each_side: purlinOverhang.toFixed(2),
      },
      north_to_south_leg_distance: totalSpanZ.toFixed(2),
      east_to_west_leg_distance: eastWestSpan.toFixed(2),
      area_bw_legs: legArea.toFixed(2),
      panel_span_area: panelArea.toFixed(2),
      panel_clearance_ground: {
        length: panelTotalLength.toFixed(2),
        breadth: panelTotalWidth.toFixed(2),
      },
      clamps: {
        mid_clamps: totalMidClamps,
        end_clamps: totalEndClamps,
      },
      fasteners: {
        base_plates: basePlates,
        base_plate_fastners: basePlates * 4,
        nut_bolt_pairs: nutBoltPairs,
        breakdown_nut_bolt_pairs: {
          rafter_to_legs: rafterToLegJoints,
          rafter_to_purlins: rafterToPurlinJoints,
        },
      },
    }),
    [tileAngleDeg, legHeightMap, rafters, purlins, tableCols, totalSpanZ],
  );

  useMemo(() => {
    setStructureData(structureSummary);
  }, [structureSummary, setStructureData]);

  function getLegColor(height) {
    return height > 3.5 ? 'yellow' : '#D3D3D3';
  }

  return (
    <group>
      {legs.map(({ x, z, height }, i) => (
        <mesh
          key={`leg-${i}`}
          position={[x, height / 2, z]}
          castShadow
          receiveShadow
        >
          <cylinderGeometry args={[legRadius, legRadius, height]} />
          <meshStandardMaterial color={getLegColor(height)} />
        </mesh>
      ))}

      {rafters.map(({ x, y, z, length, angle }, i) => (
        <mesh
          key={`rafter-${i}`}
          position={[x, y, z]}
          rotation={[-angle, 0, 0]}
          castShadow
          receiveShadow
        >
          <boxGeometry args={[0.1, 0.1, length]} />
          <meshStandardMaterial color='#D3D3D3' />
        </mesh>
      ))}

      {purlins.map(({ y, z }, i) => (
        <mesh
          key={`purlin-${i}`}
          position={[(spacingX * (tableCols - 1)) / 2, y, z]}
          castShadow
          receiveShadow
        >
          <boxGeometry
            args={[
              spacingX * (tableCols - 1) + 2 * purlinOverhang,
              purlinThickness,
              purlinThickness,
            ]}
          />
          <meshStandardMaterial color='#4a4a4a' />
        </mesh>
      ))}

      {panels.map(({ x, y, z }, i) => (
        <mesh
          key={`panel-${i}`}
          position={[x, y, z]}
          rotation={[tiltRadians + Math.PI / 2, 0, 0]}
          castShadow
          receiveShadow
        >
          <boxGeometry args={[panelWidth, panelLength, 0.1]} />
          <meshStandardMaterial
            color={panelColor}
            transparent
            opacity={0.5}
            side={2}
          />
        </mesh>
      ))}
    </group>
  );
}
